// Shared read-only person detail view — PROPS-DRIVEN. No API/query imports.
// Renders only the sections whose data keys are present (the API omits gated
// groups server-side, so an absent key == "not permitted / no data"):
//   Identity            — always (basics)
//   Physical            — person.read_pii (key present)
//   Addresses / phones / emails / emergency contacts — person.read_pii
//   Government IDs      — list of masked rows; per-row Reveal gated by
//                         can('person.read_government_ids') + onRevealGovernmentId
//   History tab         — shown when `history` provided AND can('person.read_history')

import { useState } from 'react';
import type {
  EmergencyRelationship,
  EyeColor,
  GovernmentIdType,
  HairColor,
  PersonAddress,
  PersonAddressLabel,
  PersonDetail,
  PersonEmail,
  PersonEmailLabel,
  PersonEmergencyContact,
  PersonGovernmentId,
  PersonHistoryEntry,
  PersonPhone,
  PersonPhoneLabel,
  PersonSuffix,
  PersonTitle,
} from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { can } from '../auth/store';
import { Badge, Button, StatusBadge, Table, Tabs, type TabItem } from './ui';

export interface PersonDetailViewProps {
  person: PersonDetail;
  history?: PersonHistoryEntry[] | null;
  onRevealGovernmentId?: (id: number) => Promise<string>;
  /** Hide the read-only Emails section (when the page renders an editable
   *  PersonEmailsManager instead). */
  hideEmails?: boolean;
}

// ---------- Enum label maps ----------

const TITLE_LABEL: Record<PersonTitle, string> = {
  mr: 'Mr.',
  ms: 'Ms.',
  mrs: 'Mrs.',
  dr: 'Dr.',
  mx: 'Mx.',
  other: 'Other',
};

const SUFFIX_LABEL: Record<PersonSuffix, string> = {
  jr: 'Jr.',
  sr: 'Sr.',
  ii: 'II',
  iii: 'III',
  iv: 'IV',
  other: 'Other',
};

const EYE_LABEL: Record<EyeColor, string> = {
  brown: 'Brown',
  blue: 'Blue',
  green: 'Green',
  hazel: 'Hazel',
  gray: 'Gray',
  amber: 'Amber',
  other: 'Other',
};

const HAIR_LABEL: Record<HairColor, string> = {
  black: 'Black',
  brown: 'Brown',
  blond: 'Blond',
  red: 'Red',
  gray: 'Gray',
  white: 'White',
  bald: 'Bald',
  other: 'Other',
};

const ADDRESS_LABEL: Record<PersonAddressLabel, string> = {
  home: 'Home',
  mailing: 'Mailing',
  work: 'Work',
  residence: 'Residence',
  previous: 'Previous',
  other: 'Other',
};

const PHONE_LABEL: Record<PersonPhoneLabel, string> = {
  mobile: 'Mobile',
  home: 'Home',
  work: 'Work',
  other: 'Other',
};

const EMAIL_LABEL: Record<PersonEmailLabel, string> = {
  personal: 'Personal',
  work: 'Work',
  company: 'Company',
  other: 'Other',
};

const RELATIONSHIP_LABEL: Record<EmergencyRelationship, string> = {
  spouse: 'Spouse',
  parent: 'Parent',
  child: 'Child',
  sibling: 'Sibling',
  partner: 'Partner',
  friend: 'Friend',
  other: 'Other',
};

const GOV_ID_LABEL: Record<GovernmentIdType, string> = {
  ssn: 'SSN',
  drivers_license: "Driver's License",
  passport: 'Passport',
  state_id: 'State ID',
  military_id: 'Military ID',
  other: 'Other',
};

// ---------- Formatting helpers ----------

function fmtDate(epoch: number | null | undefined): string {
  if (epoch == null) return '—';
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function fmtDateTime(epoch: number): string {
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US');
}

function fmtValue(v: unknown): string {
  if (v == null) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function fmtAddr(a: PersonAddress): string {
  if (a.formatted) return a.formatted;
  const line = [a.street1, a.street2].filter(Boolean).join(', ');
  const region = [a.city, a.state].filter(Boolean).join(', ');
  return [line, region, a.postal_code].filter(Boolean).join(' · ') || '—';
}

// ---------- Main view ----------

export function PersonDetailView(props: PersonDetailViewProps): JSX.Element {
  const { person, history, onRevealGovernmentId, hideEmails } = props;
  const showHistory = !!history && can('person.read_history');

  // Physical is part of the PII group; the API only sends these keys when the
  // viewer holds person.read_pii.
  const hasPhysical =
    person.dob != null ||
    person.height_in != null ||
    person.weight_lb != null ||
    person.eye_color != null ||
    person.hair_color != null ||
    (person.distinguishing_marks != null && person.distinguishing_marks !== '');

  const profile = (
    <div className="tab-panel" role="tabpanel">
      {/* ---------- Identity ---------- */}
      <section className="detail-card">
        <h2>Identity</h2>
        <dl className="detail-grid">
          <div>
            <dt>Title</dt>
            <dd>{person.title ? TITLE_LABEL[person.title] : '—'}</dd>
          </div>
          <div>
            <dt>Full name</dt>
            <dd>{person.full_name}</dd>
          </div>
          <div>
            <dt>Suffix</dt>
            <dd>{person.suffix ? SUFFIX_LABEL[person.suffix] : '—'}</dd>
          </div>
          <div>
            <dt>Preferred name</dt>
            <dd>{person.preferred_name ?? '—'}</dd>
          </div>
        </dl>
      </section>

      {/* ---------- Physical (person.read_pii) ---------- */}
      {hasPhysical && (
        <section className="detail-card">
          <h2>Physical descriptors</h2>
          <dl className="detail-grid">
            <div>
              <dt>Date of birth</dt>
              <dd>{fmtDate(person.dob)}</dd>
            </div>
            <div>
              <dt>Height</dt>
              <dd>{person.height_in != null ? `${person.height_in} in` : '—'}</dd>
            </div>
            <div>
              <dt>Weight</dt>
              <dd>{person.weight_lb != null ? `${person.weight_lb} lb` : '—'}</dd>
            </div>
            <div>
              <dt>Eye color</dt>
              <dd>{person.eye_color ? EYE_LABEL[person.eye_color] : '—'}</dd>
            </div>
            <div>
              <dt>Hair color</dt>
              <dd>{person.hair_color ? HAIR_LABEL[person.hair_color] : '—'}</dd>
            </div>
            <div className="detail-grid-full">
              <dt>Distinguishing marks</dt>
              <dd>{person.distinguishing_marks ?? '—'}</dd>
            </div>
          </dl>
        </section>
      )}

      {/* ---------- Addresses ---------- */}
      {person.addresses && (
        <section className="detail-card">
          <h2>Addresses</h2>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Label</Table.HeaderCell>
                <Table.HeaderCell>Address</Table.HeaderCell>
                <Table.HeaderCell>Primary</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {person.addresses.length === 0 && (
                <Table.Empty colSpan={3}>No addresses on file.</Table.Empty>
              )}
              {person.addresses.map((a: PersonAddress) => (
                <Table.Row key={a.id}>
                  <Table.Cell>{ADDRESS_LABEL[a.label]}</Table.Cell>
                  <Table.Cell>{fmtAddr(a)}</Table.Cell>
                  <Table.Cell>{a.is_primary ? <Badge tone="success">Primary</Badge> : '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </section>
      )}

      {/* ---------- Phones ---------- */}
      {person.phones && (
        <section className="detail-card">
          <h2>Phones</h2>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Label</Table.HeaderCell>
                <Table.HeaderCell>Number</Table.HeaderCell>
                <Table.HeaderCell>Primary</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {person.phones.length === 0 && (
                <Table.Empty colSpan={3}>No phones on file.</Table.Empty>
              )}
              {person.phones.map((p: PersonPhone) => (
                <Table.Row key={p.id}>
                  <Table.Cell>{PHONE_LABEL[p.label]}</Table.Cell>
                  <Table.Cell>
                    {formatPhoneDisplay(p.number)}
                    {p.extension ? ` ext. ${p.extension}` : ''}
                  </Table.Cell>
                  <Table.Cell>{p.is_primary ? <Badge tone="success">Primary</Badge> : '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </section>
      )}

      {/* ---------- Emails ---------- */}
      {!hideEmails && person.emails && (
        <section className="detail-card">
          <h2>Emails</h2>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Label</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell>Primary</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {person.emails.length === 0 && (
                <Table.Empty colSpan={3}>No emails on file.</Table.Empty>
              )}
              {person.emails.map((em: PersonEmail) => (
                <Table.Row key={em.id}>
                  <Table.Cell>
                    {EMAIL_LABEL[em.label]}
                    {em.is_company_managed && (
                      <StatusBadge status="new">Managed</StatusBadge>
                    )}
                  </Table.Cell>
                  <Table.Cell>{em.email}</Table.Cell>
                  <Table.Cell>{em.is_primary ? <Badge tone="success">Primary</Badge> : '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </section>
      )}

      {/* ---------- Emergency contacts ---------- */}
      {person.emergency_contacts && (
        <section className="detail-card">
          <h2>Emergency contacts</h2>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Priority</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Relationship</Table.HeaderCell>
                <Table.HeaderCell>Phone</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {person.emergency_contacts.length === 0 && (
                <Table.Empty colSpan={5}>No emergency contacts on file.</Table.Empty>
              )}
              {person.emergency_contacts.map((c: PersonEmergencyContact) => (
                <Table.Row key={c.id}>
                  <Table.Cell>{c.priority}</Table.Cell>
                  <Table.Cell>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</Table.Cell>
                  <Table.Cell>{RELATIONSHIP_LABEL[c.relationship]}</Table.Cell>
                  <Table.Cell>
                    {formatPhoneDisplay(c.phone)}
                    {c.alt_phone ? ` / ${formatPhoneDisplay(c.alt_phone)}` : ''}
                  </Table.Cell>
                  <Table.Cell>{c.email ?? '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </section>
      )}

      {/* ---------- Government IDs ---------- */}
      {person.government_ids && (
        <section className="detail-card">
          <h2>Government IDs</h2>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Number</Table.HeaderCell>
                <Table.HeaderCell>Issuing</Table.HeaderCell>
                <Table.HeaderCell>Expires</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {person.government_ids.length === 0 && (
                <Table.Empty colSpan={4}>No government IDs on file.</Table.Empty>
              )}
              {person.government_ids.map((g: PersonGovernmentId) => (
                <GovIdRow
                  key={g.id}
                  govId={g}
                  onReveal={onRevealGovernmentId}
                />
              ))}
            </Table.Body>
          </Table>
        </section>
      )}
    </div>
  );

  // No History tab unless the viewer holds person.read_history AND data is
  // present — render the profile directly so no single-tab strip appears.
  if (!showHistory || !history) {
    return <div className="person-detail-view">{profile}</div>;
  }

  const tabItems: TabItem[] = [
    { value: 'profile', label: 'Profile', content: profile },
    {
      value: 'history',
      label: 'History',
      content: (
        <div className="tab-panel" role="tabpanel">
          <HistoryTimeline entries={history} />
        </div>
      ),
    },
  ];

  return (
    <div className="person-detail-view">
      <Tabs items={tabItems} defaultValue="profile" />
    </div>
  );
}

// ---------- Government ID row (per-row reveal) ----------

function GovIdRow({
  govId,
  onReveal,
}: {
  govId: PersonGovernmentId;
  onReveal?: (id: number) => Promise<string>;
}): JSX.Element {
  const canReveal = can('person.read_government_ids') && !!onReveal;
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const issuing = [govId.issuing_state, govId.issuing_country].filter(Boolean).join(' · ');

  const doReveal = async (): Promise<void> => {
    if (!onReveal || busy) return;
    setBusy(true);
    try {
      const full = await onReveal(govId.id);
      setRevealed(full);
    } catch {
      /* swallow — caller surfaces the error via toast/log */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Table.Row>
      <Table.Cell>{GOV_ID_LABEL[govId.id_type]}</Table.Cell>
      <Table.Cell tone="mono">
        {revealed ?? `•••• ${govId.number_last4}`}
        {canReveal && (
          <Button
            variant="ghost"
            className="btn-sm"
            disabled={busy}
            onClick={() => {
              if (revealed) {
                setRevealed(null);
              } else {
                void doReveal();
              }
            }}
          >
            {busy ? 'Revealing…' : revealed ? 'Hide' : 'Reveal'}
          </Button>
        )}
      </Table.Cell>
      <Table.Cell>{issuing || '—'}</Table.Cell>
      <Table.Cell>{fmtDate(govId.expires_at)}</Table.Cell>
    </Table.Row>
  );
}

// ---------- History timeline ----------

function HistoryTimeline({ entries }: { entries: PersonHistoryEntry[] }): JSX.Element {
  if (entries.length === 0) {
    return <p className="muted">No change history recorded.</p>;
  }
  return (
    <ol className="rpt-timeline person-history">
      {entries.map((entry, i) => {
        const fields = Object.entries(entry.changes);
        return (
          <li key={`${entry.entity_type}-${entry.entity_id}-${entry.event_at}-${i}`} className="rpt-timeline-item">
            <div className="rpt-timeline-time">{fmtDateTime(entry.event_at)}</div>
            <div className="rpt-timeline-desc">
              <StatusBadge status="new" className="rpt-timeline-type">{entry.entity_type}</StatusBadge>{' '}
              <Badge tone="neutral">{entry.action}</Badge>{' '}
              <span className="muted small">
                {entry.changed_by_name ?? (entry.changed_by != null ? `#${entry.changed_by}` : 'system')}
              </span>
              {fields.length > 0 && (
                <dl className="detail-grid person-history-changes">
                  {fields.map(([field, diff]) => (
                    <div key={field}>
                      <dt>{field}</dt>
                      <dd>
                        <code className="mono">{fmtValue(diff.old)}</code>
                        {' → '}
                        <code className="mono">{fmtValue(diff.new)}</code>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
